/* 
 * Bloom For Women @2018
 *  All Rights Reserved.
 */


import * as Handlebars from 'handlebars';
import * as jQuery from 'jquery';
import * as linkifyHtml from "linkifyjs/html";
import { WPAjaxObject } from './WPAjaxObject';
import * as ld from "lodash";
let _ = ld.noConflict();

declare interface ChatMessage {
    /**
     * The unique id of the chat message
     */
    uuid:string;
    /**
     * The data sent through the chat
     */
    data: {text:string};
    /**
     * The individual who sent the message
     * @see ChatPerson
     */
    sender: {uuid:string, state: string};

    /**
     * The unique time this was sent.  Represented as a unix UTC nanoseconds timestamp
     */
    timetoken:number;
}

/**
 * @see https://www.pubnub.com/docs/chat-engine/reference/user
 */
declare interface ChatPerson {
    uuid:string;
}
/**
 * @see https://www.pubnub.com/docs/chat-engine/reference/chat
 */
declare interface Chat {
    emit(eventName:string, data:any) : void;
    on(eventName:string, eventData:(data:ChatMessage) => void) : void;
    search(args:{event: string,limit: number, start?:number, end?:number, includeTimetoken?:boolean, pages?:number, count?:number}) : Chat
}
declare interface ChatEventData {
    me : any;
}
/**
 *  @see https://www.pubnub.com/docs/chat-engine/reference/chatengine
 */
declare interface ChatEngine {
    connect(uid:string, person:ChatPerson) : void;
    on(eventName:string, eventData:(data:ChatEventData) => void) : void;
    Chat: new (chatName:string) => Chat;
    /**
     * Disconnects from all chats and marks them as asleep.
     */
    disconnect() : void;
}
declare class ChatEngineCore {
    static create: (options:any) => ChatEngine;
    
}

export class BloomChat {

    private chatHistoryLoadLimit:number = 25;
    /**
     * Reference to PubNub chatengine
     * @see https://www.pubnub.com/docs/chat-engine/reference/chatengine
     */
     private chatEngine:ChatEngine;

     /**
      * Reference to PubNub chat object
      * @see https://www.pubnub.com/docs/chat-engine/reference/chat
      */
     private myChat:Chat;

     /**
      * The current individual that is in the chat.
      
      * @see https://www.pubnub.com/docs/chat-engine/reference/me
      */
     private me:ChatPerson;


     /**
      * Represents an individual in the chat and is the user the current user is chatting with.
      * @see https://www.pubnub.com/docs/chat-engine/reference/user
      */
    private recipientPerson:ChatPerson;


    /**
     * Boolean notification that the receipient of the chat is currently online and chatting with the current user.
     */     
    private recipientOnline:boolean;

     /**
      * Represents the DOM node of the current user.
      */
     private meTemplate:HandlebarsTemplateDelegate<any>;

     /**
      * Represents the DOM node of the person the current user is sending messags to.
      */
     private userTemplate:HandlebarsTemplateDelegate<any>;

     /**
      * Represents the DOM node of system errors, information, and other messages sent.
      */
     private systemMessageTemplate:HandlebarsTemplateDelegate<any>;

     /**
      * Should the chat window auto scroll down to the bottom of the chat screen when a message is sent.
      */
     private autoScrollDown:boolean;

     /**
      * Are there more chat history messages to be loaded when the user scrolls to the top of the chat.
      */
     private hasMoreMessages:boolean;

     /**
      * Are we currently in a network request searching for additional chat messages.
      */
     private isSearchingMessages:boolean;

     /**
      * Reference pointer to the search object retrieving chat history messages.
      */
     private searchEventEmitter:any;

     /**
      * Keeps track of the last chat message timestamp between search message loads.
      * As this is unix UTC nanoseconds it is assumed to be unique per message.
      */
     private scrollTopLastTimestamp:number;

     /**
      * Array of all the messages currently loaded in the chat application for the current user and recipient.
      */
     private messages:ChatMessage[];

     /**
      * Handlebars template string for system messages
      */
     private systemMessage:string = `<div class='alert alert-{{type}}'>{{message}}</div>`;

     /**
      * Handlebars template string for messages the current user has sent.
      */
     private messageTemplate:string = `<li class="clearfix">
     <div class="message-data align-right" id='chat-{{uuid}}'>
         <span class="message-data-time">{{datetime}}</span> &nbsp; &nbsp;
         <span class="message-data-name">{{user.first}}</span> <i class="fa fa-circle me"></i>
     </div>
     <div class="message other-message float-right">
         {{{messageOutput}}}
     </div>
 </li>`;

    /**
     * Handlebars template for messages sent to the current user from the recipient.
     */
    private responseTemplate:string = `<li id='chat-{{uuid}}'>
    <div class="message-data">
        <span class="message-data-name"><i class="fa fa-circle online"></i> {{user.first}}</span>
        <span class="message-data-time">{{datetime}}</span>
    </div>
    <div class="message my-message">
        {{{messageOutput}}}
    </div>
</li>`;
    
    /**
     * Creates a Bloom Chat object.
     * @param publishKey The API key for publishing messages to the platform
     * @param subscribeKey The API key for subscribing to messages for the chat platform
     * @param ajaxObject The wordpress ajax object for communicating with the wordpress server
     * @param window The global browser window object.
     */
     constructor(private publishKey:string, private subscribeKey:string, private ajaxObject:WPAjaxObject, private window:Window) {
        this.recipientOnline = false;
        this.hasMoreMessages = false;
        this.isSearchingMessages = false;
        this.autoScrollDown = true; //default to this.
        this.scrollTopLastTimestamp = 0; // set to default so we don't scroll when things are loaded.
        this.messages = [];
     }

     /**
      * Starts a chat session for the current user (chatPerson) in the given chatRoom with chat messages being sent to
      * the recipientPerson.  If recipientPerson is offline, offline message notifications are sent to the wordpress server for the
      * recipientPerson to have notifications handled there.
      * @param chatPerson The current user who is chatting.
      * @param chatRoom The room that the user wants to chat in
      * @param recipientPerson The recipient of the current user's chat messages.
      */
     public init(chatPerson:ChatPerson, chatRoom:string, recipientPerson:ChatPerson) {
        this.recipientPerson = recipientPerson;
        this.chatEngine = ChatEngineCore.create({
            publishKey: this.publishKey,
            subscribeKey: this.subscribeKey
        });
        this.setupTemplates();

        // connect to ChatEngine with our generated user
        this.chatEngine.connect(chatPerson.uuid, chatPerson);
        this.displaySystemMessage("Loading messages...", "info");

        this.setupChangeUser();
        // when ChatEngine is booted, it returns your new User as `data.me`
        this.chatEngine.on('$.ready', (data) => {

            // store my new user as `me`
            this.me = data.me;

            // create a new ChatEngine Chat
            this.myChat = new this.chatEngine.Chat(chatRoom);
            this.setupErrorHandlers(this.myChat, this.chatEngine);
            this.setupUserPrescence(this.myChat, recipientPerson);
            this.setupMessageReceiveEvent(this.myChat);
            this.setupChatConnection(this.myChat);
            this.setupChatScrollHandlers();
            this.setupChatMessageSend();
            this.setupChatExitHandler(this.chatEngine);

        });

     }

    /**
     * Sends a message the current user has typed in to the chat room, handling any offline notifications
     * if necessary.  Clears the current chat input box once the message has been sent.
     */
    public sendMessage() {

        // get the message text from the text input
        let message = (jQuery('#message-to-send').val() as string).trim();
    
        // if the message isn't empty
        if (message.length) {
        
            // emit the `message` event to everyone in the Chat
            this.myChat.emit('message', {
                text: message
            });

            if (!this.recipientOnline) {
                this.sendNotificationToRecipient(message);
            }

            // clear out the text input
            jQuery('#message-to-send').val('');
        }
        
        // stop form submit from bubbling
        return false;
    
    }

    /**
     * Creates all of the handlebar templates to be used in the chat application.
     */
    private setupTemplates() {
        this.meTemplate = Handlebars.compile(this.messageTemplate);
        this.userTemplate = Handlebars.compile(this.responseTemplate);  
        this.systemMessageTemplate = Handlebars.compile(this.systemMessage);
    }

    /**
     * Register event callbacks to handle changing the current recipient of the chat.
     */
    private setupChangeUser() {
          // make it so we can switch between users on the chat.
          jQuery("#changeChatUser").change((event:JQuery.TriggeredEvent) => {
            let selectBox = event.currentTarget as HTMLSelectElement;
            if (!selectBox.selectedOptions.length) {
                return;
            }

            let first = selectBox.selectedOptions[0];
            let chatRoom = jQuery(first).data("chat-room");
            if (chatRoom) {
                window.location.href = chatRoom;
                event.stopPropagation();
            }
        });
        let chatRoomUsers = 
        jQuery(".chat-room-user").click((event:JQuery.TriggeredEvent) => {
            let chatRoom = jQuery(event.currentTarget).data('chat-room');
            if (chatRoom) {
                window.location.href = chatRoom;
                event.stopPropagation();
            }
        });
    }

    /**
     * Creates the event handler for messages received from PubNub 
     * @param chat The current PubNub chat instance
     */
     private setupMessageReceiveEvent(chat:Chat) {

            // when we recieve messages in this chat, render them
            chat.on('message', (message:ChatMessage) => {
                this.handleChatHistoryEvent(message);
            });
     }

     /**
      * Connects to PubNub and handles all of the chat history setup.
      * @param chat The current PubNub chat instance
      */
     private setupChatConnection(chat:Chat) {

            // wait for our chat to be connected to the internet
            chat.on('$.connected', () => {
                this.setupChatHistory(chat);
            });
     }

     /**
      * Creates event handlers for the user scrolling (finger, or mouse) in the chat window
      * to load more chat messages.
      */
     private setupChatScrollHandlers() {

            // make sure we don't listen to scroll all the time
            jQuery(".chat-history").scroll(_.throttle(() => {
                this.watchScroll();
            }, 250));
     }

     /**
      * Registers the send message event for the current user to submit their chat messages.
      */
     private setupChatMessageSend() {
            // bind our "send" button and return key to send message
            jQuery('#sendMessage').on('submit', () => {
                this.sendMessage();
                this.scrollToBottom();
                return false;
            });

     }

     /**
      * Registers an event handler when the user closes the browser or leaves the page to close
      * down all the chat connections.  Otherwise it can take a minute or longer for the browser to
      * close the connection to pubnub.
      * @param chatEngine The PubNub object for interacting with their chat engine api
      */
     private setupChatExitHandler(chatEngine:ChatEngine) {
            // if we don't disconnect it creates a 1 minute delay before the browser shuts everything off.
            this.window.addEventListener("beforeunload", () => {
                console.log("Disconnecting from chats");
                chatEngine.disconnect();
            })

     }

     /**
      * Registers with the PubNub presence API to detect when the user goes online or offline so we can update their current
      * status in the DOM.
      * @param chat The current PubNub chat instance
      * @param recipientPerson The person recieving the chat messages we want to detect if they are offline/online
      */
     private setupUserPrescence(chat:Chat, recipientPerson:ChatPerson) {
        // when a user comes online, render them in the online list
        chat.on('$.online.*', (data:any) => {
            var recipientOnline = false;
            if (data.user.uuid == recipientPerson.uuid) {
                recipientOnline = true;
                this.setStatusIndicator(recipientOnline);
            }
        });

        // when a user goes offline, remove them from the online list
        chat.on('$.offline.*', (data:any) => {
            var recipientOnline = true;
            if (data.user.uuid == this.recipientPerson.uuid) {
                recipientOnline = false;
                this.setStatusIndicator(recipientOnline);
            }
        });
        
     }

     /**
      * Launches the search history to load the chat for the current user.
      * @param chat The current chat instance for PubNUb
      */
     private setupChatHistory(chat:Chat) {
        // search for 50 old `message` events
        this.searchEventEmitter = chat.search({
            event: 'message',  // the event we are searching on
            limit: 1000,  // note limit is the MAXIMUM messages we will ever display in this chat screen
            pages: 1,  // technically there are two pages as pages goes from 0 then to 1.
            count: this.chatHistoryLoadLimit  // the current number of messages to load per page (note there are two pages so the initial messages will be count * 2)
        });
        this.searchEventEmitter.on('message', (message:ChatMessage) => {
            this.handleChatHistoryEvent(message, true);
        });
        this.searchEventEmitter.on('$.search.pause', (data:any) => {
            this.hideSystemMessage();
            this.hasMoreMessages = true;
            this.isSearchingMessages = false;
            this.scrollIfSaved();
        });
        this.searchEventEmitter.on('$.search.start', (data:any) => {
            this.displaySystemMessage("Loading messages...", "info");
            this.isSearchingMessages = true;
        });
        this.searchEventEmitter.on('$.search.finish', (data:any) => {
            this.hideSystemMessage();
            this.isSearchingMessages = false;
            this.hasMoreMessages = false;
            this.scrollIfSaved();
        });
     }

     /**
      * Setup the system error handlers for displaying errors when something goes wrong.
      * @param chat The PubNub chat instance
      * @param chatEngine The PubNub main api object for interacting with their system.
      */
     private setupErrorHandlers(chat:Chat, chatEngine:ChatEngine) {
        chatEngine.on('$.error.*', (payload) => {
            console.error(payload);
            this.displaySystemMessage("A system error occurred.  Check your internet connection", "danger");
         });

        chat.on('$.error.presence', (data:any) => {
            this.displaySystemMessage("There was a network error.  Please check your internet connection.", "danger");
        });
        chat.on('$.error.publish', (data:any) => {
            this.displaySystemMessage("There was an error in sending your message.  Check your internet connection and try again", "danger");
        });

     }

     /**
      * Clear out any status messages from our system to the user.
      */
     private hideSystemMessage() {
         jQuery('.chat-system-message-container').html('');
     }
     /**
      * Displays a message to the current user for the current chat system state such as loading messages, errors, etc.
      * @param message The system message to display to the current user.
      * @param type The kind of message this is.
      */
     private displaySystemMessage(message:string, type:"info"|"danger"|"success") {
        this.hideSystemMessage();
        let el = this.systemMessageTemplate({
            type: type,
            message: message
        });
        jQuery('.chat-system-message-container').append(el);
     }

     /**
      * If we have a message at the top of the screen that a user has scrolled to and the system is now loading more messages
      * scroll back to this message once the system has loaded those messages.
      */
     private scrollIfSaved() {
         if (this.scrollTopLastTimestamp > 0) {
            //  let offsetTop = jQuery("#chat-" + this.scrollTopLastTimestamp).offset().top;
            // taken from : https://stackoverflow.com/a/2906009/7884612
            let chatContainer = jQuery(".chat-history");
            let scrollTop = jQuery("#chat-" + this.scrollTopLastTimestamp).offset().top - chatContainer.offset().top + chatContainer.scrollTop();
            chatContainer.scrollTop(scrollTop);
             this.scrollTopLastTimestamp = 0;
         }
     }
    
    /**
     * Renders the message to the screen and adds it to our internal array.
     * @param message The PubNub chat message object.
     * @param isHistory Is this an old message loaded from the search or a new message just sent.
     */
    private handleChatHistoryEvent(message:ChatMessage, isHistory:boolean = false) {

        // console.log(data)
        
        // when messages are returned, render them like normal messages
        this.messages.unshift(message);
        this.renderMessage(message, isHistory);
    }

    /**
     * Monitors the scrolling of the user and if they've reached the bottom we trigger the auto scroll down behavior.
     * If the user has scrolled to the top we trigger loading more messages unless we've already got them all.
     */
    private watchScroll() {
        this.autoScrollDown = this.hasScrollReachedBottom();

        if (jQuery(".chat-history").scrollTop() == 0) {
            if (!this.isSearchingMessages && this.hasMoreMessages) {
                // move to the next processing loop so we can handle any updates.
                _.defer(() => {
                    if (this.messages.length) {
                        this.scrollTopLastTimestamp = this.messages[0].timetoken;
                    }
                    this.displaySystemMessage("Loading messages...", "info");
                    this.searchEventEmitter.next();
                });
            }
        }
    }

    /**
     * Has the user reached the bottom of the chat history screen.
     */
    private hasScrollReachedBottom() {
        let element = jQuery('.chat-history');
        return element.scrollTop() + element.innerHeight() >= element.prop('scrollHeight')
    };

    /**
     * Renders the message to the screen either prepending it if this is a history chat message
     * or appending it if this is a new message.
     * @param message The PubNub chat message object.
     * @param isHistory Is this an old message loaded from the search or a new message just sent.
     */
    private renderMessage(message:ChatMessage, isHistory = false) {

        // use the generic user template by default
        let template = this.userTemplate;
    
        // if I happened to send the message, use the special template for myself
        if (message.sender.uuid == this.me.uuid) {
            template = this.meTemplate;
        }
        console.log(message);
        // TODO: stephen need to check security on this replace links to see if there is anything we
        // want out as we are allowing all HTML to be injected here...
        let text = this.replaceLinks(message.data.text);
        let date = new Date(message.timetoken / (10**4));
    
        let el = template({
            uuid: message.timetoken,
            messageOutput: text,
            datetime: this.getFormattedDateTime(date),
            user: message.sender.state
        });
      
        // render the message
        if(isHistory) {
          jQuery('.chat-history ul').prepend(el); 
        } else {
          jQuery('.chat-history ul').append(el); 
        }
        if (this.autoScrollDown) {
            this.scrollToBottom();
        }
    };
    
    /**
     * Scroll the user to the bottom of the chat history.
     */
    private scrollToBottom() {
        jQuery('.chat-history').scrollTop(jQuery('.chat-history')[0].scrollHeight);
    };
    
    /**
     * Retrieves the date object for the user using (Today in the mesage if its the current date).  Uses the local time of the
     * current user.
     * @param date The current date
     */
    private getFormattedDateTime(date:Date) {
        let today = new Date();
        let dateString = "Today";
        if (date.getDay() != today.getDay() || today.getMonth() != today.getMonth() || date.getFullYear() != today.getFullYear()) {
            dateString = date.toLocaleDateString();
        }
        return date.toLocaleTimeString() + ", " + dateString;
        // return date.toLocaleTimeString();
        // return date.toLocaleTimeString().replace(/([\d]+:[\d]{2})(:[\d]{2})(.*)/, "$1$3");
    };

    /**
     * Sends a notification message to the wordpress server with the message the current user attempted to send to them.
     * @param message The message to send to the recipeitn who is offline.
     */
    private sendNotificationToRecipient(message:string) {
        jQuery.post(this.ajaxObject.ajax_url, {
                action: 'bloom_coach_notify_offline_person'
                ,personId: this.recipientPerson.uuid
                ,message:message
            }, null, 'json')
        .done((resp) => {
            console.log("Recipient[",this.recipientPerson.uuid,"] notified");
        })
        .fail((error) => {
            console.error("Recipient[",this.recipientPerson.uuid,"] failed to be notified.  Error:", error);
        })
        .always(() => {});
    }

    /**
     * Takes a text string and scans for any html messages converting them to actual links.
     * @param message The message to convert links in.
     */
    private replaceLinks(message:string) {
        // todo: stephen handle link conversion here using something like 
        // https://github.com/SoapBox/linkifyjs/
        // for whatever reason the typescript is really not liking the way this imports
        // with the webpack builder, so we are ignoring it and moving forward.
        // @ts-ignore
        var newLinks:string = linkifyHtml(message, {defaultProtocol: 'https'});
        // console.log("message: " , message);
        return newLinks;
        // return message;
    }

    /**
     * Shows the online or offline status indicator on the DOM based upon the online parameter.
     * @param online If the recipient is online (true), or if the recipient is offline
     */
    private setStatusIndicator(online:boolean) {
        this.recipientOnline = online;
        if (online === true) {
            jQuery("#online-status").removeClass('hidden');
            jQuery("#offline-status").addClass('hidden');
        }
        else {
            jQuery("#online-status").addClass('hidden');
            jQuery("#offline-status").removeClass('hidden');
        }
    }
 }
